import {
  useState,
  useCallback,
  useEffect,
  React,
  useRef,
} from 'react';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import { Container } from '@mui/system';
import Typography from '@mui/material/Typography';
import {
  Dialog,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
} from '@mui/material';
import { useParams, useSearchParams } from 'react-router-dom';
import { Survey } from 'survey-react-ui';
import './surveytheme.css';
import { Model, StylesManager } from 'survey-core';
import Lottie from 'lottie-react';
import $ from 'jquery';
import CloseIcon from '@mui/icons-material/Close';

import { getFeedback, saveFeedback } from '../../services/feedback';
import MessageView from '../MessageView/MessageView';
import FormHeader from '../FormHeader/FormHeader';
import Loader from '../Loader/Loader';
import MovingArrows from '../../static/movingArrows.json';
import {
  dialogContentTheme,
  dialogTextTheme,
  globalStylesObject,
  constants,
} from './materialExports';
import YesNoButtonGroup from './YesNoButtonGroup';
import OptionButtonGroup from './OptionButtonGroup';
import RatingStars from './RatingStars';
import RatingSmiley from './RatingSmiley';

const EXPERIENCE_MAP = {
  1: 'NoPain',
  2: 'HurtsALittle',
  3: 'HurtsALittleMore',
  4: 'HurtsEvenMore',
  5: 'HurtsWholeLot',
  6: 'HurtsWorst',
};

const isOccOrder = metadata => !!(metadata?.occ_id);

const FeedbackForm = () => {
  window.$ = $;
  window.jQuery = $;

  const { uuid } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [metaData, updateMetaData] = useState({});
  const [status, updateStatus] = useState('loading');
  const [formData, updateFormData] = useState({});
  const [ratingValue, updateRatingValue] = useState(0);
  const [hoverValue, updateHoverValue] = useState(0);
  const [cleanlinessRating, updateCleanlinessRating] = useState(0);
  const [cleanlinessHover, updateCleanlinessHover] = useState(0);
  const [experienceRating, updateExperienceRating] = useState(0);
  const [prickCount, updatePrickCount] = useState(null);
  const [isFirstOpen, setIsFirstOpen] = useState(true);
  const [surveyDefinition, setSurveyDefinition] = useState(null);
  const [expandedFlag, setExpanded] = useState(false);
  const additionalRef = useRef([]);
  const isOcc = isOccOrder(metaData);
  // modal condition based on in-page formData
  const {
    emedic_appearance,
    emedic_needle_prick_experience,
    emedic_single_needle_prick,
    emedic_prick_count,
    emedic_nps,
    emedic_feedback_comments,
    store_cleanliness_nps,
  } = formData;

  const buildForm = (overrides = {}) => ({
    emedic_single_needle_prick,
    emedic_prick_count,
    emedic_appearance,
    emedic_needle_prick_experience,
    emedic_feedback_comments,
    emedic_nps,
    store_cleanliness_nps,
    ...overrides,
  });
  const {
    watchMedia,
    modalSubmitFeedback,
    modalChangeRating,
    submitFeedback,
    accordionOnClick,
    expanded,
    ratingOne,
    ratingTwo,
    ratingYes,
    star,
    stars,
  } = constants;
  const [matches, setMatches] = useState(
    window.matchMedia(watchMedia).matches,
  );

  /**
   * Determines if the feedback improvement modal should be shown.
   * Modal shows when user had a negative experience AND gave low rating (1-2 stars).
   *
   * Two scenarios trigger the modal:
   * 1. Negative experience: eMedic in uniform, severe pain (4-6), multiple pricks, low NPS
   * 2. Incomplete form: User only provided low NPS rating without other details
   */
  const shouldShowModal = () => {
    const hasLowRating = emedic_nps === ratingOne || emedic_nps === ratingTwo;
    // eslint-disable-next-line eqeqeq
    const hasNoComments = emedic_feedback_comments == undefined;

    if (!hasLowRating || !hasNoComments) {
      return false;
    }

    // Scenario 1: User had negative experience with service
    const hadNegativeExperience = emedic_appearance === ratingYes
      && ['HurtsEvenMore', 'HurtsWholeLot', 'HurtsWorst'].includes(emedic_needle_prick_experience)
      && emedic_single_needle_prick === 'No';

    // Scenario 2: User only rated NPS without filling other fields
    // eslint-disable-next-line eqeqeq
    const onlyProvidedRating = emedic_appearance == undefined
      // eslint-disable-next-line eqeqeq
      && emedic_needle_prick_experience == undefined
      // eslint-disable-next-line eqeqeq
      && emedic_single_needle_prick == undefined;

    return hadNegativeExperience || onlyProvidedRating;
  };

  const rating = useCallback(
    (val) => {
      updateRatingValue(val);
      updateHoverValue(val);
      const form = buildForm({ emedic_nps: val });
      updateFormData(form);
      saveFeedback(uuid, token, true, form);
    },
    [
      emedic_single_needle_prick,
      emedic_prick_count,
      emedic_appearance,
      emedic_needle_prick_experience,
      emedic_feedback_comments,
      store_cleanliness_nps,
      uuid,
      token,
    ],
  );

  const cleanliness = useCallback(
    (val) => {
      updateCleanlinessRating(val);
      updateCleanlinessHover(val);
      const form = buildForm({ store_cleanliness_nps: val });
      updateFormData(form);
      saveFeedback(uuid, token, true, form);
    },
    [
      emedic_single_needle_prick,
      emedic_prick_count,
      emedic_appearance,
      emedic_needle_prick_experience,
      emedic_feedback_comments,
      emedic_nps,
      store_cleanliness_nps,
      uuid,
      token,
    ],
  );
  useEffect(() => {
    window
      .matchMedia(watchMedia)
      .addEventListener('change', event => setMatches(event.matches));
  }, []);

  // root logic control function by case
  async function handleClickByCase(exp, model) {
    switch (exp) {
      case modalSubmitFeedback: {
        const formFeedbackMode = buildForm({ emedic_feedback_comments: '' });
        updateFormData(formFeedbackMode);
        setIsFirstOpen(false);
        await additionalRef.current.click();
        window.scroll({
          top: document.body.offsetHeight,
          left: 0,
          behavior: 'auto',
        });
        break;
      }
      case modalChangeRating: {
        setIsFirstOpen(false);
        const formReset = buildForm({ emedic_nps: 0 });
        updateFormData(formReset);
        updateRatingValue(0);
        updateHoverValue(0);
        break;
      }
      case submitFeedback:
        if (!shouldShowModal() || !isFirstOpen) {
          model.completeLastPage();
        }
        break;
      case accordionOnClick:
        if (!expandedFlag) {
          const surveyDefExpanded = { ...surveyDefinition };
          surveyDefExpanded[1].pages[0].elements[0].state = expanded;
          setSurveyDefinition(...[surveyDefinition], [surveyDefExpanded]);
          setExpanded(true);
        }
        break;
      default:
        break;
    }
  }

  const surveyComplete = useCallback(
    (sender) => {
      sender.valuesHash.emedic_nps = ratingValue;
      try {
        const res = saveFeedback(uuid, token, false, sender.data);
        if (res) {
          updateStatus('completed');
        }
      } catch (e) {
        updateStatus('error');
      }
    },
    [ratingValue],
  );

  const surveyAutosave = useCallback(
    (sender) => {
      sender.valuesHash.emedic_nps = ratingValue;
      try {
        const res = saveFeedback(uuid, token, true, sender.data);
        if (res) {
          const { data } = sender || {};
          const {
            emedic_appearance: emedic_appearance_res,
            emedic_needle_prick_experience: emedic_needle_prick_experience_res,
            emedic_single_needle_prick: emedic_single_needle_prick_res,
            emedic_prick_count: emedic_prick_count_res,
            emedic_nps: emedic_nps_res,
            emedic_feedback_comments: emedic_feedback_comments_res,
            store_cleanliness_nps: store_cleanliness_nps_res,
          } = data;
          updateFormData({
            emedic_single_needle_prick: emedic_single_needle_prick_res,
            emedic_prick_count: emedic_prick_count_res,
            emedic_appearance: emedic_appearance_res,
            emedic_needle_prick_experience: emedic_needle_prick_experience_res,
            emedic_feedback_comments: emedic_feedback_comments_res,
            emedic_nps: emedic_nps_res,
            store_cleanliness_nps: store_cleanliness_nps_res,
          });
        }
      } catch (e) {
        console.info('form autosave failed', e.message);
      }
    },
    [ratingValue],
  );

  const loadFeedback = async () => {
    try {
      const res = await getFeedback(uuid, token);
      if (res) {
        updateMetaData(res?.feedback_metadata);
        setSurveyDefinition(res?.survey?.survey_definition);
        if (res.response_data != null) {
          updateFormData(res.response_data);
        }
        if (res.status === 'closed') {
          updateStatus('locked');
        } else {
          updateStatus('filling');
        }
      }
    } catch (e) {
      if (e.message === '423') {
        updateStatus('locked');
        return;
      }
      if (['404', '403', '401'].indexOf(e.message) !== -1) {
        updateStatus('notfound');
        return;
      }
      updateStatus('error');
    }
  };

  useEffect(() => {
    if (status === 'loading') {
      loadFeedback();
    }
  }, [status]);
  useEffect(() => {
    updateRatingValue(formData.emedic_nps || 0);
    updateHoverValue(formData.emedic_nps || 0);
    updateCleanlinessRating(formData.store_cleanliness_nps || 0);
    updateCleanlinessHover(formData.store_cleanliness_nps || 0);
    if (formData.emedic_needle_prick_experience) {
      const experienceKey = Object.keys(EXPERIENCE_MAP).find(
        key => EXPERIENCE_MAP[key] === formData.emedic_needle_prick_experience,
      );
      if (experienceKey) {
        updateExperienceRating(Number(experienceKey));
      }
    }
    if (formData.emedic_prick_count) {
      updatePrickCount(formData.emedic_prick_count === 'One' ? null : formData.emedic_prick_count);
    }
  }, [formData]);

  const isFormValid = () => {
    const hasNeedlePrickExperience = !!emedic_needle_prick_experience;
    const hasNPS = !!emedic_nps && emedic_nps > 0;
    const hasAppearance = !!emedic_appearance;
    const hasSingleNeedlePrick = !!emedic_single_needle_prick;
    let hasPrickCount = false;
    if (emedic_single_needle_prick === 'Yes') {
      hasPrickCount = emedic_prick_count === 'One';
    } else if (emedic_single_needle_prick === 'No') {
      hasPrickCount = emedic_prick_count === 'Two' || emedic_prick_count === 'MoreThanTwo';
    }

    const isBaseValid = hasNeedlePrickExperience
      && hasSingleNeedlePrick
      && hasPrickCount
      && hasNPS
      && hasAppearance;

    if (isOcc) {
      const hasStoreCleanliness = !!store_cleanliness_nps && store_cleanliness_nps > 0;
      return isBaseValid && hasStoreCleanliness;
    }

    return isBaseValid;
  };

  if (status === 'filling') {
    StylesManager.applyTheme('defaultV2');

    // surveyjs settings definitions
    const model = new Model(surveyDefinition[0]);
    const additionalAccordion = new Model(surveyDefinition[1]);
    model.data = formData;
    model.onComplete.add(surveyComplete);
    model.onValueChanged.add(surveyAutosave);
    model.showNavigationButtons = 'none';
    additionalAccordion.data = formData;
    additionalAccordion.onComplete.add(surveyComplete);
    additionalAccordion.onValueChanged.add(surveyAutosave);
    additionalAccordion.showNavigationButtons = 'none';
    return (
      <div className="form-container">
        <Dialog
          open={shouldShowModal() && isFirstOpen}
          PaperProps={{
            style: matches
              ? globalStylesObject.dialogPaperProps
              : globalStylesObject.dialogPaperPropsResponsive,
          }}
          onClose={() => setIsFirstOpen(false)}
        >
          <Container sx={globalStylesObject.textContainer}>
            <CloseIcon
              sx={globalStylesObject.closeButton}
              onClick={() => setIsFirstOpen(false)}
            />
          </Container>
          <DialogTitle
            sx={matches ? dialogTextTheme(true) : dialogTextTheme(false)}
          >
            What could be better?
          </DialogTitle>
          <DialogContent sx={{ letterSpacing: 0 }}>
            <DialogContentText sx={dialogContentTheme()}>
              You have rated us <b>{ratingValue} {ratingValue === ratingOne ? star : stars}</b>,
            </DialogContentText>
            <DialogContentText sx={dialogContentTheme()}>
              Please let us know how we can improve
            </DialogContentText>
            <DialogContentText sx={dialogContentTheme()}>
              our services!
            </DialogContentText>
            <Button
              onClick={() => handleClickByCase(modalSubmitFeedback)}
              sx={globalStylesObject.shareFeedBackButton}
            >
              Share Feedback
            </Button>
            <Button
              onClick={() => handleClickByCase(modalChangeRating)}
              sx={globalStylesObject.changeRatingButton}
            >
              <b>
                <u>I want to change my rating</u>
              </b>
            </Button>
          </DialogContent>
        </Dialog>
        <FormHeader
          metadata={metaData}
        />
        <Container
          disableGutters
          sx={globalStylesObject.containerSx}
        >
          <Card elevation={0} sx={globalStylesObject.cardRoot}>
            <CardContent sx={globalStylesObject.cardContent}>
              <Typography
                sx={globalStylesObject.cardTypography}
                gutterBottom
              >
                How was the needle prick experience?
              </Typography>
              <RatingSmiley
                rating={experienceRating}
                onSelect={(val) => {
                  updateExperienceRating(val);
                  const newForm = buildForm({
                    emedic_single_needle_prick: null,
                    emedic_prick_count: null,
                    emedic_needle_prick_experience: EXPERIENCE_MAP[val],
                  });
                  saveFeedback(uuid, token, true, newForm);
                  updateFormData(newForm);
                }}
              />
              {experienceRating > 0 && (
                <>
                  <Typography
                    sx={{
                      ...globalStylesObject.cardTypography,
                      marginTop: '12px',
                    }}
                    gutterBottom
                  >
                    Was it a single needle prick?
                  </Typography>
                  <YesNoButtonGroup
                    selectedValue={formData.emedic_single_needle_prick}
                    onYes={() => {
                      updatePrickCount(null);
                      const newForm = buildForm({
                        emedic_single_needle_prick: 'Yes',
                        emedic_prick_count: 'One',
                      });
                      saveFeedback(uuid, token, true, newForm);
                      updateFormData(newForm);
                    }}
                    onNo={() => {
                      const newForm = buildForm({
                        emedic_single_needle_prick: 'No',
                      });
                      saveFeedback(uuid, token, true, newForm);
                      updateFormData(newForm);
                    }}
                  />
                  {formData.emedic_single_needle_prick === 'No' && (
                    <>
                      <Typography
                        sx={{
                          ...globalStylesObject.cardTypography,
                          marginTop: '24px',
                        }}
                        gutterBottom
                      >
                        How many pricks did it take for blood to be drawn?
                      </Typography>
                      <OptionButtonGroup
                        options={[
                          {
                            key: 'two',
                            label: 'Two',
                            onClick: () => {
                              updatePrickCount('Two');
                              const newForm = buildForm({
                                emedic_prick_count: 'Two',
                              });
                              saveFeedback(uuid, token, true, newForm);
                              updateFormData(newForm);
                            },
                            selected: prickCount === 'Two',
                            selectedSx: globalStylesObject.ButtonSelectedNo,
                            defaultSx: globalStylesObject.ButtonEmptyNo,
                            selectedLabelSx: globalStylesObject.TextNo,
                            defaultLabelSx: globalStylesObject.Text,
                          },
                          {
                            key: 'more-than-two',
                            label: 'More than two',
                            onClick: () => {
                              updatePrickCount('MoreThanTwo');
                              const newForm = buildForm({
                                emedic_prick_count: 'MoreThanTwo',
                              });
                              saveFeedback(uuid, token, true, newForm);
                              updateFormData(newForm);
                            },
                            selected: prickCount === 'MoreThanTwo',
                            selectedSx: globalStylesObject.ButtonSelectedNo,
                            defaultSx: globalStylesObject.ButtonEmptyNo,
                            selectedLabelSx: globalStylesObject.TextNo,
                            defaultLabelSx: globalStylesObject.Text,
                          },
                        ]}
                        sx={{ height: '40px' }}
                      />
                    </>
                  )}
                </>
              )}
            </CardContent>
          </Card>
          <Card elevation={0} sx={globalStylesObject.cardRootTwo}>
            <CardContent sx={globalStylesObject.cardContent}>
              <Typography
                sx={globalStylesObject.cardTypography}
                gutterBottom
              >
                {isOcc
                  ? 'Was our eMedic professional and helpful?'
                  : 'How would you rate your experience with our eMedic?'}
              </Typography>
              <RatingStars
                rating={ratingValue}
                hoverValue={hoverValue}
                onSelect={rating}
                onHover={updateHoverValue}
                onHoverEnd={() => updateHoverValue(ratingValue || 0)}
              />
            </CardContent>
          </Card>
          {isOcc && (
            <Card elevation={0} sx={globalStylesObject.cardRootTwo}>
              <CardContent sx={globalStylesObject.cardContent}>
                <Typography
                  sx={globalStylesObject.cardTypography}
                  gutterBottom
                >
                  How clean did the store feel to you?
                </Typography>
                <RatingStars
                  rating={cleanlinessRating}
                  hoverValue={cleanlinessHover}
                  onSelect={cleanliness}
                  onHover={updateCleanlinessHover}
                  onHoverEnd={() => updateCleanlinessHover(cleanlinessRating || 0)}
                />
              </CardContent>
            </Card>
          )}
          <Card elevation={0} sx={globalStylesObject.cardRootTwo}>
            <CardContent sx={globalStylesObject.cardContent}>
              <Typography
                sx={globalStylesObject.cardTypography}
                gutterBottom
              >
                Was the eMedic in uniform and well groomed?
              </Typography>
              <YesNoButtonGroup
                selectedValue={formData.emedic_appearance}
                onYes={() => {
                  const newForm = buildForm({ emedic_appearance: 'Yes' });
                  saveFeedback(uuid, token, true, newForm);
                  updateFormData(newForm);
                }}
                onNo={() => {
                  const newForm = buildForm({ emedic_appearance: 'No' });
                  saveFeedback(uuid, token, true, newForm);
                  updateFormData(newForm);
                }}
              />
            </CardContent>
          </Card>
        </Container>
        <Container
          disableGutters
          sx={globalStylesObject.accordionContainer}
          ref={additionalRef}
          onClick={() => { handleClickByCase(accordionOnClick); }}
        >
          <Survey model={additionalAccordion} />
        </Container>
        <div className="button-container">
          <button
            className="submit-button"
            type="submit"
            disabled={!isFormValid()}
            onClick={() => {
              handleClickByCase(submitFeedback, model);
            }}
            style={{
              opacity: isFormValid() ? 1 : 0.5,
              cursor: isFormValid() ? 'pointer' : 'not-allowed',
            }}
          >
            <div className="button-horizontal">
              Submit feedback
              <Lottie
                style={{ marginTop: '-23px' }}
                animationData={MovingArrows}
                loop
                height={40}
                width={40}
                className="lottie-animation"
              />
            </div>
          </button>
        </div>
      </div>
    );
  }

  if (status === 'loading') {
    return <Loader />;
  }

  if (status === 'completed') {
    return (
      <MessageView
        message="Thank you for the feedback"
        submessage="Your feedback will help us improve your sample collection experience and serve you better"
        icon="success"
      />
    );
  }

  if (status === 'error') {
    return (
      <MessageView
        message="We are unable to open the feedback form temporarily. Please retry in few minutes."
        icon="error"
      />
    );
  }

  if (status === 'notfound') {
    return (
      <MessageView
        message="Feedback link does not exist"
        submessage="The link you clicked may be broken or the page may have been removed."
        icon="exclamation"
      />
    );
  }

  if (status === 'locked') {
    return (
      <MessageView
        message="You've already submitted your feedback"
        icon="success"
      />
    );
  }
};

export default FeedbackForm;
